import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslate, useNotify, usePermissions } from 'react-admin';
import { Box, Typography, Card, CardContent, Button, MenuItem, FormControl, InputLabel, Select, CircularProgress } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import { Payments as PaymentsIcon } from '@mui/icons-material';
import { apiClient } from '../../utils/apiClient';
import { API_CONFIG } from '../../config/api';
import { hasPermission } from '../../utils/hasPermission';

const METHODS = [
    { key: 'charge.decimal_places.card', label: 'resources.sites.kioskChargeRounding.card' },
    { key: 'charge.decimal_places.qr', label: 'resources.sites.kioskChargeRounding.qr' },
] as const;

type MethodKey = (typeof METHODS)[number]['key'];
type Choice = 'inherit' | '1' | '2';

interface KioskSettingDefinition {
    key: string;
    defaultValue: unknown;
}

interface KioskSettingCell {
    key: string;
    value: unknown;
}

const requireChoice = (value: unknown, key: string): Choice => {
    if (value === 1) return '1';
    if (value === 2) return '2';
    throw new Error(`${key} holds ${JSON.stringify(value)}, not 1 or 2`);
};

const bothInherited = (): Record<MethodKey, Choice> => ({ 'charge.decimal_places.card': 'inherit', 'charge.decimal_places.qr': 'inherit' });

interface SiteKioskChargeRoundingPanelProps {
    siteId: string;
}

export const SiteKioskChargeRoundingPanel: React.FC<SiteKioskChargeRoundingPanelProps> = ({ siteId }) => {
    const translate = useTranslate();
    const notify = useNotify();
    const { permissions, isLoading: permissionsLoading } = usePermissions();

    const permissionList = permissions as string[] | null | undefined;
    const canRead = !permissionsLoading && hasPermission(permissionList, 'kiosk-devices:read:all');
    const canWrite = !permissionsLoading && hasPermission(permissionList, 'kiosk-devices:write:all');

    const settingsUrl = `${API_CONFIG.endpoints.backend.sites}/${siteId}/kiosk-settings`;

    const [loading, setLoading] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);
    const [inherited, setInherited] = useState<Record<MethodKey, Choice>>(bothInherited);
    const [loaded, setLoaded] = useState<Record<MethodKey, Choice>>(bothInherited);
    const [choices, setChoices] = useState<Record<MethodKey, Choice>>(bothInherited);
    const [busy, setBusy] = useState(false);

    const translateRef = useRef(translate);
    translateRef.current = translate;

    const reportError = useCallback(
        (error: any) => {
            const apiError = error?.body || error;
            notify(apiError?.message || translateRef.current('resources.sites.kioskChargeRounding.loadError'), { type: 'error' });
        },
        [notify]
    );

    const load = useCallback(async () => {
        const [definitionsResponse, siteResponse, systemResponse] = await Promise.all([
            apiClient.get<{ data: KioskSettingDefinition[] }>(API_CONFIG.endpoints.backend.kioskSettingDefinitions),
            apiClient.get<{ data: KioskSettingCell[] }>(settingsUrl),
            apiClient.get<{ data: KioskSettingCell[] }>(API_CONFIG.endpoints.backend.kioskSettings),
        ]);
        const nextInherited = bothInherited();
        const nextLoaded = bothInherited();
        for (const method of METHODS) {
            const definition = definitionsResponse.data.find((candidate) => candidate.key === method.key);
            if (!definition) throw new Error(`The server has no ${method.key} setting`);
            const systemCell = systemResponse.data.find((candidate) => candidate.key === method.key);
            const siteCell = siteResponse.data.find((candidate) => candidate.key === method.key);
            nextInherited[method.key] = requireChoice(systemCell ? systemCell.value : definition.defaultValue, method.key);
            if (siteCell) nextLoaded[method.key] = requireChoice(siteCell.value, method.key);
        }
        setInherited(nextInherited);
        setLoaded(nextLoaded);
        setChoices(nextLoaded);
    }, [settingsUrl]);

    useEffect(() => {
        if (!canRead || !siteId) return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                await load();
                if (!cancelled) setLoadFailed(false);
            } catch (error: any) {
                if (cancelled) return;
                setLoadFailed(true);
                reportError(error);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [canRead, siteId, load, reportError]);

    if (!canRead) return null;

    const placesLabel = (choice: Choice) =>
        translate(choice === '2' ? 'resources.sites.kioskChargeRounding.two' : 'resources.sites.kioskChargeRounding.one');
    const changed = METHODS.some((method) => choices[method.key] !== loaded[method.key]);

    const handleSave = async () => {
        setBusy(true);
        try {
            for (const method of METHODS) {
                const choice = choices[method.key];
                if (choice === loaded[method.key]) continue;
                const url = `${settingsUrl}/${encodeURIComponent(method.key)}`;
                if (choice === 'inherit') await apiClient.delete(url);
                else await apiClient.put(url, { value: Number(choice) });
            }
            notify(translate('resources.sites.kioskChargeRounding.saved'), { type: 'success' });
        } catch (error: any) {
            reportError(error);
        } finally {
            try {
                await load();
            } catch (error: any) {
                reportError(error);
            }
            setBusy(false);
        }
    };

    const body = () => {
        if (loading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                </Box>
            );
        }
        if (loadFailed) {
            return (
                <Typography variant="body2" color="error">
                    {translate('resources.sites.kioskChargeRounding.loadError')}
                </Typography>
            );
        }
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                    {translate('resources.sites.kioskChargeRounding.intro')}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                    {METHODS.map((method) => (
                        <FormControl key={method.key} fullWidth>
                            <InputLabel id={`site-kiosk-${method.key}`}>{translate(method.label)}</InputLabel>
                            <Select
                                labelId={`site-kiosk-${method.key}`}
                                label={translate(method.label)}
                                value={choices[method.key]}
                                onChange={(event: SelectChangeEvent) =>
                                    setChoices((current) => ({ ...current, [method.key]: event.target.value as Choice }))
                                }
                                disabled={!canWrite || busy}
                            >
                                <MenuItem value="inherit">
                                    {translate('resources.sites.kioskChargeRounding.inherited', { places: placesLabel(inherited[method.key]) })}
                                </MenuItem>
                                <MenuItem value="1">{translate('resources.sites.kioskChargeRounding.one')}</MenuItem>
                                <MenuItem value="2">{translate('resources.sites.kioskChargeRounding.two')}</MenuItem>
                            </Select>
                        </FormControl>
                    ))}
                </Box>
                {canWrite && (
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button
                            variant="contained"
                            onClick={handleSave}
                            disabled={busy || !changed}
                            sx={{
                                backgroundColor: '#00C853',
                                '&:hover': { backgroundColor: '#00A843' },
                                textTransform: 'none',
                                fontWeight: 600,
                            }}
                        >
                            {translate('resources.sites.kioskChargeRounding.save')}
                        </Button>
                        {!changed && (
                            <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                                {translate('resources.sites.kioskChargeRounding.unchanged')}
                            </Typography>
                        )}
                    </Box>
                )}
            </Box>
        );
    };

    return (
        <Card sx={{ mb: 3, overflow: 'hidden' }}>
            <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <PaymentsIcon sx={{ mr: 2, color: '#00C853' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {translate('resources.sites.kioskChargeRounding.title')}
                    </Typography>
                </Box>
                {body()}
            </CardContent>
        </Card>
    );
};
